import api from '../../api';
import type { ApiResponse, PageResult } from '../../types';
import type { TaskWorkMode } from '../../../types';

export type { ApiResponse };

export interface TaskUserVO {
  id: string;
  userId: string;
  userName: string;
  roleType: string;
  roleTypeName: string;
  description?: string;
  startDate: string;
  endDate: string;
  days: number;
  amount: number;
  status: string;
}

export interface TaskVO {
  id: string;
  projectId: string;
  projectName: string;
  milestoneId?: string;
  workMode?: TaskWorkMode;
  parentId: string;
  companyId: string;
  companyName: string;
  scheduleId: string;
  name: string;
  description: string;
  type: string;
  status: string;
  priority: string;
  assigneeId: string;
  assigneeName: string;
  reporterId: string;
  reporterName: string;
  ownerId: string;
  ownerName: string;
  startDate: string;
  endDate: string;
  dueDate: string;
  actualStartDate: string;
  actualEndDate: string;
  actualFinishDate: string;
  days: number;
  progress: number;
  estimatedHours: number;
  actualHours: number;
  budget: number;
  actualCost: number;
  amount: number;
  amountType: string;
  location: string;
  remark: string;
  attachmentUrl: string;
  sortOrder: number;
  isTemplate: number;
  createTime: string;
  updateTime: string;
  taskUsers: TaskUserVO[];
}

export interface TaskQueryParams {
  pageNum?: number;
  pageSize?: number;
  keyword?: string;
  projectId?: string;
  companyId?: string;
  ownerId?: string;
  assigneeId?: string;
  status?: string;
  type?: string;
}

export interface TaskUserSaveParams {
  id?: string;
  userId: string;
  userName: string;
  roleType?: string;
  roleTypeName?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  days?: number;
  amount?: number;
  status?: string;
}

export interface TaskSaveParams {
  id?: string;
  projectId?: string;
  projectName?: string;
  milestoneId?: string;
  workMode?: TaskWorkMode;
  parentId?: string;
  companyId?: string;
  companyName?: string;
  scheduleId?: string;
  name: string;
  description?: string;
  type?: string;
  status?: string;
  priority?: string;
  assigneeId?: string;
  assigneeName?: string;
  reporterId?: string;
  reporterName?: string;
  ownerId?: string;
  ownerName?: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  actualFinishDate?: string;
  days?: number;
  progress?: number;
  estimatedHours?: number;
  actualHours?: number;
  budget?: number;
  actualCost?: number;
  amount?: number;
  amountType?: string;
  location?: string;
  remark?: string;
  attachmentUrl?: string;
  sortOrder?: number;
  isTemplate?: number;
  taskUsers?: TaskUserSaveParams[];
}

export const getTasks = (params: TaskQueryParams): Promise<ApiResponse<PageResult<TaskVO>>> => {
  return api.get('/project/tasks', { params });
};

export const getTaskById = (id: string): Promise<ApiResponse<TaskVO>> => {
  return api.get(`/project/tasks/${id}`);
};

export const createTask = (data: TaskSaveParams): Promise<ApiResponse<string>> => {
  return api.post('/project/tasks', data);
};

export const updateTask = (id: string, data: TaskSaveParams): Promise<ApiResponse<void>> => {
  return api.put(`/project/tasks/${id}`, data);
};

export const deleteTask = (id: string): Promise<ApiResponse<void>> => {
  return api.delete(`/project/tasks/${id}`);
};

export const batchDeleteTasks = (ids: string[]): Promise<ApiResponse<void>> => {
  return api.delete('/project/tasks/batch', { data: ids });
};

export const completeTask = (id: string): Promise<ApiResponse<void>> => {
  return api.post(`/project/tasks/${id}/complete`);
};

export const updateTaskStatus = (id: string, status: string): Promise<ApiResponse<void>> => {
  return api.put(`/project/tasks/${id}/status`, null, { params: { status } });
};

export const reopenTask = (id: string): Promise<ApiResponse<void>> => {
  return updateTaskStatus(id, 'in_progress');
};

/** 2.2 催办：通知任务负责人尽快处理（同任务同催办人 5 分钟限一次） */
export const nudgeTask = (id: string): Promise<ApiResponse<void>> => {
  return api.post(`/project/tasks/${id}/nudge`);
};

interface TaskDictOption {
  code: string;
  name: string;
  icon?: string;
  color?: string;
  description?: string;
}

export const TASK_TYPES: TaskDictOption[] = [
  { code: 'development', name: '开发实施', icon: 'CodeOutlined', color: '#531dab' },
  { code: 'training', name: '培训授课', icon: 'BookOutlined', color: '#1890ff' },
  { code: 'assessment', name: '人才测评', icon: 'AuditOutlined', color: '#52c41a' },
  { code: 'workshop', name: '研讨催化', icon: 'TeamOutlined', color: '#722ed1' },
  { code: 'interview', name: '调研访谈', icon: 'SolutionOutlined', color: '#faad14' },
  { code: 'consulting', name: '咨询服务', icon: 'CustomerServiceOutlined', color: '#13c2c2' },
  { code: 'online', name: '线上辅导', icon: 'VideoCameraOutlined', color: '#eb2f96' },
  { code: 'teaching_assistant', name: '助教服务', icon: 'UserOutlined', color: '#fa541c' },
  { code: 'visual_recording', name: '视觉记录', icon: 'PictureOutlined', color: '#2f54eb' },
  { code: 'other', name: '其他', icon: 'MoreOutlined', color: '#8c8c8c' },
];

export const AMOUNT_TYPES: TaskDictOption[] = [
  { code: 'service', name: '服务费', description: '咨询服务或培训服务费用' },
  { code: 'tax', name: '税费', description: '相关税费' },
  { code: 'transport', name: '交通费', description: '差旅交通费用' },
  { code: 'accommodation', name: '住宿费', description: '差旅住宿费用' },
  { code: 'copyright', name: '版权费', description: '版权或知识产权费用' },
  { code: 'transfer', name: '走账费用', description: '走账相关费用' },
  { code: 'other', name: '其他', description: '其他费用' },
];

export const TASK_STATUS: TaskDictOption[] = [
  { code: 'pending', name: '待处理', color: '#8c8c8c' },
  { code: 'in_progress', name: '进行中', color: '#1890ff' },
  { code: 'completed', name: '已完成', color: '#52c41a' },
  { code: 'overdue', name: '已逾期', color: '#ff4d4f' },
];

export const PRIORITY_OPTIONS: TaskDictOption[] = [
  { code: 'high', name: '高', color: '#ff4d4f' },
  { code: 'medium', name: '中', color: '#faad14' },
  { code: 'low', name: '低', color: '#52c41a' },
];
